import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useEditMode,
  useProperty,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { btnPrimary, clean, hasValue, linkTarget } from '../utils/content';

interface Floorplan {
  floorplan_name?: string | null;
  floorplan_description?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
}

const bgDef: SlotGroupDefinition = {
  label: 'Background Photo',
  description: 'Decorative backdrop behind the bedroom-type circles. Darker photos read best.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
  },
  generator: {
    prompt: 'A wide exterior, landscape, or neighborhood photo with calm, darker tones so white text stays legible over it.',
    context: 'Full-bleed background behind the "Room to Roam" bedroom-type circles on the home page, under a dark overlay.',
  },
};

const studioPhotoDef: SlotGroupDefinition = {
  label: 'Studio Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'An interior photo that best represents the studio homes here. Leave empty if there are no studios.',
    context: 'Circle 1 of 5 (Studio) in the home page bedroom-type row. Different photo from the other circles.',
  },
};
const studioLinkDef: SlotGroupDefinition = {
  label: 'Studio Card',
  fields: {
    label: { label: 'Label', type: 'text', defaultValue: 'Studio' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans?bedroom=studio' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    _beds: { label: 'Bedrooms', type: 'text', defaultValue: '0' },
  },
};

const oneBedPhotoDef: SlotGroupDefinition = {
  label: 'One Bedroom Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'An interior photo that best represents the one-bedroom homes here.',
    context: 'Circle 2 of 5 (One Bedroom) in the home page bedroom-type row. Different photo from the other circles.',
  },
};
const oneBedLinkDef: SlotGroupDefinition = {
  label: 'One Bedroom Card',
  fields: {
    label: { label: 'Label', type: 'text', defaultValue: 'One Bedroom' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans?bedroom=one' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    _beds: { label: 'Bedrooms', type: 'text', defaultValue: '1' },
  },
};

const twoBedPhotoDef: SlotGroupDefinition = {
  label: 'Two Bedroom Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'An interior photo that best represents the two-bedroom homes here.',
    context: 'Circle 3 of 5 (Two Bedroom) in the home page bedroom-type row. Different photo from the other circles.',
  },
};
const twoBedLinkDef: SlotGroupDefinition = {
  label: 'Two Bedroom Card',
  fields: {
    label: { label: 'Label', type: 'text', defaultValue: 'Two Bedroom' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans?bedroom=two' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    _beds: { label: 'Bedrooms', type: 'text', defaultValue: '2' },
  },
};

const threeBedPhotoDef: SlotGroupDefinition = {
  label: 'Three Bedroom Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'An interior photo that best represents the three-bedroom homes here. Leave empty if there are none.',
    context: 'Circle 4 of 5 (Three Bedroom) in the home page bedroom-type row. Different photo from the other circles.',
  },
};
const threeBedLinkDef: SlotGroupDefinition = {
  label: 'Three Bedroom Card',
  fields: {
    label: { label: 'Label', type: 'text', defaultValue: 'Three Bedroom' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans?bedroom=three' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    _beds: { label: 'Bedrooms', type: 'text', defaultValue: '3' },
  },
};

const townhomePhotoDef: SlotGroupDefinition = {
  label: 'Townhome Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo of the townhome-style residences here. Leave empty if the property has no townhomes.',
    context: 'Circle 5 of 5 (Townhomes) in the home page bedroom-type row. Different photo from the other circles.',
  },
};
const townhomeLinkDef: SlotGroupDefinition = {
  label: 'Townhome Card',
  fields: {
    label: { label: 'Label', type: 'text', defaultValue: 'Townhomes' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans?bedroom=townhome' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    _beds: { label: 'Bedrooms', type: 'text', defaultValue: 'townhome' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Floor Plans Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** Which bedroom keys ("0", "1", ..., "townhome") the property's floor plans cover. */
function offeredTypes(plans: Floorplan[]): Set<string> {
  const keys = new Set<string>();
  for (const p of plans) {
    const min = parseInt(clean(p.floorplan_beds_min), 10);
    const max = parseInt(clean(p.floorplan_beds_max), 10);
    if (!isNaN(min)) {
      const hi = isNaN(max) ? min : max;
      for (let b = min; b <= hi; b++) keys.add(String(Math.min(b, 3)));
    }
    const text = `${clean(p.floorplan_name)} ${clean(p.floorplan_description)}`.toLowerCase();
    if (text.includes('town')) keys.add('townhome');
  }
  return keys;
}

/** Fallback when floor plans are not loaded yet (or load live): the property's bedroom range. */
function rangeTypes(property: Record<string, unknown> | null | undefined): Set<string> | null {
  const min = parseInt(clean(property?.property_bedrooms_min), 10);
  const max = parseInt(clean(property?.property_bedrooms_max), 10);
  if (isNaN(min) && isNaN(max)) return null;
  const lo = isNaN(min) ? max : min;
  const hi = isNaN(max) ? min : max;
  const keys = new Set<string>();
  for (let b = lo; b <= hi; b++) keys.add(String(Math.min(b, 3)));
  return keys;
}

interface CardProps {
  photo: Record<string, string | null>;
  link: Record<string, string | null>;
  offered: Set<string> | null;
  isEditing: boolean;
  photoSlot: ReactNode;
  linkSlot: (content: ReactNode) => ReactNode;
}

function TypeCard({ photo, link, offered, isEditing, photoSlot, linkSlot }: CardProps) {
  const key = clean(link._beds);
  const available = !offered || !key || offered.has(key);
  if ((!available || !hasValue(link.label)) && !isEditing) return null;
  return (
    <li className={cn('w-[44%] sm:w-[30%] lg:w-[17%]', !available && 'opacity-40')}>
      <a href={link.url || '/floor-plans'} target={linkTarget(link.url, link._target)} className="group block text-center no-underline">
        <div className="js-zoom mx-auto aspect-square w-full max-w-[190px] overflow-hidden rounded-full bg-muted ring-0 ring-white/70 transition-[transform,box-shadow] duration-500 ease-out group-hover:-translate-y-1.5 group-hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.7)] group-hover:ring-4">
          {photoSlot}
          {!photo.image && <span className="sr-only">{link.label}</span>}
        </div>
        {linkSlot(
          <span className="mt-4 block font-body text-[17px] font-light uppercase tracking-[0.04em] text-[color:var(--vibe-color-text-on-secondary)] transition-[letter-spacing] duration-500 ease-out group-hover:tracking-[0.1em] sm:text-[19px]">
            {link.label}
          </span>,
        )}
      </a>
    </li>
  );
}

export function FloorplanTypes() {
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const heading = useContentToken('floorplantypes.heading');
  const bg = useSlotGroup('floorplantypes.background', bgDef);
  const cta = useSlotGroup('floorplantypes.cta', ctaDef);
  const plans = useCollectionBinding<Floorplan>('floorplantypes.floorplans', { collection: 'floorplans' });
  const property = useProperty() as unknown as Record<string, unknown>;
  const offered = plans && plans.length > 0 ? offeredTypes(plans) : rangeTypes(property);

  const studioPhoto = useSlotGroup('floorplantypes.studio_photo', studioPhotoDef);
  const studioLink = useSlotGroup('floorplantypes.studio_card', studioLinkDef);
  const onePhoto = useSlotGroup('floorplantypes.one_bed_photo', oneBedPhotoDef);
  const oneLink = useSlotGroup('floorplantypes.one_bed_card', oneBedLinkDef);
  const twoPhoto = useSlotGroup('floorplantypes.two_bed_photo', twoBedPhotoDef);
  const twoLink = useSlotGroup('floorplantypes.two_bed_card', twoBedLinkDef);
  const threePhoto = useSlotGroup('floorplantypes.three_bed_photo', threeBedPhotoDef);
  const threeLink = useSlotGroup('floorplantypes.three_bed_card', threeBedLinkDef);
  const townPhoto = useSlotGroup('floorplantypes.townhome_photo', townhomePhotoDef);
  const townLink = useSlotGroup('floorplantypes.townhome_card', townhomeLinkDef);

  const img = (p: Record<string, string | null>) => (
    <VibesitesImage url={p.image || ''} alt={p.alt || ''} sizes="190px" className="h-full w-full object-cover" />
  );

  return (
    <section className="relative isolate overflow-hidden bg-secondary py-16 md:py-20">
      <EditableSlotGroup groupPath="floorplantypes.background" definition={bgDef}>
        <div className="js-parallax absolute inset-0 -z-10">
          <VibesitesImage url={bg.image || ''} alt="" sizes="100vw" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-secondary opacity-70" />
        </div>
      </EditableSlotGroup>

      <div className="mx-auto max-w-[1180px] px-6 text-center lg:px-10">
        <EditableToken path="floorplantypes.heading" as="h2" className="js-reveal js-heading text-[30px] text-[color:var(--vibe-color-text-on-secondary)] sm:text-[35px]">
          {heading}
        </EditableToken>

        <EditableCollectionBinding bindingPath="floorplantypes.floorplans" label="Floor Plans">
          <ul className="js-stagger js-d1 mt-10 flex flex-wrap justify-center gap-x-[4%] gap-y-10 lg:gap-x-[3.5%]">
            <TypeCard
              photo={studioPhoto}
              link={studioLink}
              offered={offered}
              isEditing={isEditing}
              photoSlot={<EditableSlotGroup groupPath="floorplantypes.studio_photo" definition={studioPhotoDef} className="h-full">{img(studioPhoto)}</EditableSlotGroup>}
              linkSlot={(c) => <EditableSlotGroup groupPath="floorplantypes.studio_card" definition={studioLinkDef}>{c}</EditableSlotGroup>}
            />
            <TypeCard
              photo={onePhoto}
              link={oneLink}
              offered={offered}
              isEditing={isEditing}
              photoSlot={<EditableSlotGroup groupPath="floorplantypes.one_bed_photo" definition={oneBedPhotoDef} className="h-full">{img(onePhoto)}</EditableSlotGroup>}
              linkSlot={(c) => <EditableSlotGroup groupPath="floorplantypes.one_bed_card" definition={oneBedLinkDef}>{c}</EditableSlotGroup>}
            />
            <TypeCard
              photo={twoPhoto}
              link={twoLink}
              offered={offered}
              isEditing={isEditing}
              photoSlot={<EditableSlotGroup groupPath="floorplantypes.two_bed_photo" definition={twoBedPhotoDef} className="h-full">{img(twoPhoto)}</EditableSlotGroup>}
              linkSlot={(c) => <EditableSlotGroup groupPath="floorplantypes.two_bed_card" definition={twoBedLinkDef}>{c}</EditableSlotGroup>}
            />
            <TypeCard
              photo={threePhoto}
              link={threeLink}
              offered={offered}
              isEditing={isEditing}
              photoSlot={<EditableSlotGroup groupPath="floorplantypes.three_bed_photo" definition={threeBedPhotoDef} className="h-full">{img(threePhoto)}</EditableSlotGroup>}
              linkSlot={(c) => <EditableSlotGroup groupPath="floorplantypes.three_bed_card" definition={threeBedLinkDef}>{c}</EditableSlotGroup>}
            />
            <TypeCard
              photo={townPhoto}
              link={townLink}
              offered={offered}
              isEditing={isEditing}
              photoSlot={<EditableSlotGroup groupPath="floorplantypes.townhome_photo" definition={townhomePhotoDef} className="h-full">{img(townPhoto)}</EditableSlotGroup>}
              linkSlot={(c) => <EditableSlotGroup groupPath="floorplantypes.townhome_card" definition={townhomeLinkDef}>{c}</EditableSlotGroup>}
            />
          </ul>
        </EditableCollectionBinding>

        <EditableSlotGroup groupPath="floorplantypes.cta" definition={ctaDef} className="js-reveal js-d2">
          <a href={cta.url || '/floor-plans'} target={linkTarget(cta.url, cta._target)} className={`${btnPrimary} mt-12`}>
            {cta.label || 'View Floor Plans'}
          </a>
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplanTypes',
  displayName: 'Bedroom Types',
  tokens: {
    'floorplantypes.heading': { label: 'Heading', type: 'text', defaultValue: 'Room to Roam' },
  },
  slotGroups: {
    'floorplantypes.background': bgDef,
    'floorplantypes.studio_photo': studioPhotoDef,
    'floorplantypes.studio_card': studioLinkDef,
    'floorplantypes.one_bed_photo': oneBedPhotoDef,
    'floorplantypes.one_bed_card': oneBedLinkDef,
    'floorplantypes.two_bed_photo': twoBedPhotoDef,
    'floorplantypes.two_bed_card': twoBedLinkDef,
    'floorplantypes.three_bed_photo': threeBedPhotoDef,
    'floorplantypes.three_bed_card': threeBedLinkDef,
    'floorplantypes.townhome_photo': townhomePhotoDef,
    'floorplantypes.townhome_card': townhomeLinkDef,
    'floorplantypes.cta': ctaDef,
  },
  collectionBindings: {
    'floorplantypes.floorplans': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description: 'Used to hide bedroom types this property does not offer.',
    },
  },
};

(FloorplanTypes as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplanTypes;
